import React, { useState, useEffect } from "react";
import { Form, Input, Radio, Button, message, Spin } from "antd";
import { doc, setDoc, getDoc } from "firebase/firestore";
import { firestore } from "config/firebase";
import { useAuthContext } from "contexts/AuthContext";

const ManagePaymentMethod = ({ next, prev }) => {
  const { user } = useAuthContext();
  const [loading, setLoading] = useState(false);
  const [cards, setCards] = useState([]);
  const [selectedCard, setSelectedCard] = useState(null);

  useEffect(() => {
    // Fetch existing cards from Firestore
    const fetchCards = async () => {
      if (user) {
        const userDoc = await getDoc(doc(firestore, 'users', user.uid));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          setCards(userData.cards || []);
        }
      }
    };
    fetchCards();
  }, [user]);

  const handleNext = async (values) => {
    setLoading(true);
    try {
      if (!user) {
        throw new Error("User is not authenticated");
      }

      const newCard = {
        paymentMethod: values.paymentMethod,
        cardNumber: values.cardNumber,
        cardName: values.cardName,
        expiryDate: values.expiryDate,
        cvv: values.cvv,
      };

      const updatedCards = [...cards, newCard];

      // Save updated cards to Firestore under the user's document
      await setDoc(doc(firestore, 'users', user.uid), { ...user, cards: updatedCards });

      message.success("Card saved successfully!");
      next(); // Proceed to the next step
    } catch (error) {
      console.error("Error saving card:", error);
      message.error("Failed to save card. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container-fluid">
      <h3 style={{ fontWeight: "600", marginBottom: "24px" }}>Payment Method</h3>

      {cards.length > 0 && (
        <>
          <h4>Select an Existing Card</h4>
          <div className="mb-4">
            {cards.map((card, index) => (
              <div
                key={index}
                className={`p-4 rounded-3 bg-light mb-4 ${selectedCard === index ? 'border border-primary' : ''}`}
                onClick={() => setSelectedCard(index)}
              >
                <h5>{card.cardName}</h5>
                <p className="mb-0">**** **** **** {card.cardNumber.slice(-4)}</p>
                <p className="mb-0">Expires {card.expiryDate}</p>
              </div>
            ))}
          </div>
          <Button
            type="primary"
            className="px-5 py-3"
            onClick={next}
            disabled={loading || selectedCard === null}
          >
            {loading ? <Spin /> : 'Use Selected Card'}
          </Button>
          <hr />
          <h4>Or Add a New Card</h4>
        </>
      )}

      <Form
        name="payment"
        layout="vertical"
        initialValues={{ paymentMethod: "card" }}
        onFinish={handleNext}
      >
        <Form.Item
          name="paymentMethod"
          label="Select a payment method"
          style={{ fontWeight: "600" }}
        >
          <Radio.Group>
            <Radio value="card" style={{ display: "block", marginBottom: "12px" }}>
              Debit/Credit Card
            </Radio>
            <Radio value="googlePay" style={{ display: "block", marginBottom: "12px" }}>
              Google Pay
            </Radio>
            <Radio value="paypal" style={{ display: "block", marginBottom: "12px" }}>
              Paypal
            </Radio>
            <Radio value="cod" style={{ display: "block" }}>
              Cash on Delivery
            </Radio>
          </Radio.Group>
        </Form.Item>

        <div>
          <Form.Item
            name="cardNumber"
            label="Card Number"
            rules={[{ required: true, message: "Please enter your card number" }]}
          >
            <Input placeholder="3897 222X 1900 3890" size="large" />
          </Form.Item>

          <Form.Item
            name="cardName"
            label="Card Name"
            rules={[{ required: true, message: "Please enter the name on your card" }]}
          >
            <Input placeholder="Robert Fox" size="large" />
          </Form.Item>

          <div style={{ display: "flex", justifyContent: "space-between", gap: "16px" }}>
            <Form.Item
              style={{ width: "100%" }}
              name="expiryDate"
              label="Expiry Date"
              rules={[{ required: true, message: "Please enter the expiry date" }]}
            >
              <Input placeholder="09/26" size="large" />
            </Form.Item>

            <Form.Item
              style={{ width: "100%" }}
              name="cvv"
              label="CVV"
              rules={[{ required: true, message: "Please enter your CVV" }]}
            >
              <Input placeholder="•••" size="large" />
            </Form.Item>
          </div>
        </div>

        <div style={{ display: "flex", flexDirection: "column", marginTop: "24px" }}>
          <hr />
          <Button
            type="primary"
            htmlType="submit"
            style={{ padding: "24px 112px", marginTop: "24px" }}
            loading={loading}
          >
            Save and Use New Card
          </Button>
          <br />
          <Button
            style={{ padding: "24px 112px", marginTop: "24px" }}
            onClick={prev}
          >
            Go Back
          </Button>
        </div>
      </Form>
    </div>
  );
};

export default ManagePaymentMethod;
